import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  FileText,
  CheckCircle2,
  ChevronRight,
  Menu,
  // Platform module icons -- lucide dynamic map
  FilePlus2, ShieldCheck, Gavel, Scale, Repeat, ClipboardCheck,
  Mic, MessagesSquare, Shield, UserCheck, Route,
  UserPlus, BadgeCheck, Receipt, ClipboardList, Bell, Users,
  Database, AlertTriangle, BarChart3, PieChart, Network, FileDown,
  GitBranch, FileCheck2, Bot, AlertCircle,
  Upload, Lock, Signature, History, Search, Tags,
  PenLine, Globe, Accessibility, WifiOff,
  Server, ArrowLeftRight, Key,
} from "lucide-react";

import { PLATFORM_DETAILS, PLATFORM_SLUGS } from "../data/platformDetails";
import { NAV_LINKS, PDF_URL } from "../data/site";
import Logo from "./Logo";

const ICONS = {
  FilePlus2, ShieldCheck, Gavel, Scale, Repeat, ClipboardCheck,
  Mic, MessagesSquare, Shield, UserCheck, Route, FileText,
  UserPlus, BadgeCheck, Receipt, ClipboardList, Bell, Users,
  Database, AlertTriangle, BarChart3, PieChart, Network, FileDown,
  GitBranch, FileCheck2, Bot, AlertCircle,
  Upload, Lock, Signature, History, Search, Tags,
  PenLine, Globe, Accessibility, WifiOff,
  Server, ArrowLeftRight, Key,
};

function DetailNav() {
  return (
    <nav className="fixed top-0 w-full z-[100] border-b border-slate-800/60 bg-slate-950/85 backdrop-blur-md">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Logo className="h-11 md:h-14 w-auto object-contain" />
        </Link>

        <div className="hidden md:flex items-center gap-8 font-mono text-[11px] tracking-widest uppercase text-slate-400">
          {NAV_LINKS.map((l) => (
            <Link key={l.href} to={`/${l.href}`} className="nav-link">{l.label}</Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/#contact"
            className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400 hover:text-slate-950 transition-colors"
          >
            Initiate
          </Link>
        </div>

        <Link to="/" className="md:hidden p-2 text-cyan-300" aria-label="Back">
          <ArrowLeft size={18} />
        </Link>
      </div>
    </nav>
  );
}

function DetailFooter({ platform }) {
  return (
    <footer className="relative border-t border-slate-800/70 bg-slate-950 mt-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Logo className="h-14 w-auto object-contain" />
          </div>
          <p className="mt-5 text-sm text-slate-400 max-w-md leading-relaxed">
            {platform ? `${platform.name} is one of eight packaged platforms Eco Sweeps delivers to Delaware agencies and DoD contract vehicles.` : "Technical modernization for Delaware state agencies and Department of Defense contract vehicles."}
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="eyebrow-muted mb-4">Other Platforms</div>
          <ul className="space-y-3">
            {PLATFORM_SLUGS.filter((s) => s !== platform?.slug).slice(0, 5).map((s) => (
              <li key={s}>
                <Link className="text-sm text-slate-300 hover:text-cyan-300 transition-colors" to={`/platforms/${s}`}>
                  {PLATFORM_DETAILS[s].name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="eyebrow-muted mb-4">Contact</div>
          <div className="text-sm text-slate-300 leading-relaxed">
            1-800-201-8582<br />
            support@eco-sweeps.com<br />
            Wilmington, DE
          </div>
          <a
            href={PDF_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-cyan-300 link-underline"
          >
            Capability statement <ArrowUpRight size={12} />
          </a>
        </div>
      </div>
      <div className="border-t border-slate-800/70">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row justify-between gap-3">
          <div className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase">
            © 2026 Eco Sweeps LLC · All rights reserved
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase">
            Public release · Distribution A
          </div>
        </div>
      </div>
    </footer>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <DetailNav />
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="eyebrow mb-4">/ 404</div>
          <h1 className="text-3xl font-semibold text-slate-100 tracking-tight mb-4">
            Platform not found
          </h1>
          <p className="text-sm text-slate-400 mb-8">
            The platform slug in the URL doesn't match any of the eight packaged platforms we currently deliver.
          </p>
          <Link
            to="/#portfolio"
            className="inline-flex items-center gap-2 px-5 py-3 bg-cyan-400 text-slate-950 text-[12px] font-mono uppercase tracking-[0.2em] hover:bg-white transition-colors"
          >
            Back to portfolio <ArrowUpRight size={12} />
          </Link>
        </div>
      </main>
    </div>
  );
}

export default function PlatformDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const platform = PLATFORM_DETAILS[slug];

  useEffect(() => {
    if (platform) {
      document.title = `${platform.name} · Eco Sweeps LLC`;
    } else {
      document.title = "Platform not found · Eco Sweeps LLC";
    }
    window.scrollTo(0, 0);
  }, [platform]);

  if (!platform) return <NotFound />;

  const idx = PLATFORM_SLUGS.indexOf(slug);
  const prev = PLATFORM_SLUGS[(idx - 1 + PLATFORM_SLUGS.length) % PLATFORM_SLUGS.length];
  const next = PLATFORM_SLUGS[(idx + 1) % PLATFORM_SLUGS.length];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <DetailNav />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-14 md:pb-20 px-6 md:px-10 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 20% 30%, rgba(56,189,248,0.16) 0%, rgba(56,189,248,0) 60%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto">
          <button
            onClick={() => navigate("/#portfolio")}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-cyan-300 transition-colors mb-8"
          >
            <ArrowLeft size={14} /> Back to Portfolio
          </button>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="pill">
                  <span className="dot bg-cyan-400" />
                  {platform.tag}
                </span>
                <span className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">
                  Platform · {platform.code}
                </span>
                <span className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">
                  {platform.category}
                </span>
              </div>
              <h1 className="text-[36px] md:text-[52px] leading-[1.05] tracking-tight font-semibold text-slate-50">
                {platform.name}
              </h1>
              <p className="mt-5 text-lg md:text-xl text-cyan-200/90 tracking-tight leading-snug max-w-3xl">
                {platform.headline}
              </p>
              <p className="mt-6 text-slate-400 text-sm md:text-base leading-relaxed max-w-2xl">
                {platform.description}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-cyan-400 text-slate-950 text-[12px] font-mono uppercase tracking-[0.2em] hover:bg-white transition-colors"
                >
                  <Calendar size={14} /> Scope this platform
                </a>
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-slate-700 text-slate-200 text-[12px] font-mono uppercase tracking-[0.2em] hover:border-cyan-400/60 hover:text-cyan-200 transition-colors"
                >
                  <FileText size={14} /> Capability statement <ArrowUpRight size={12} />
                </a>
              </div>
            </div>

            {/* Key stats card */}
            <aside className="lg:col-span-4">
              <div className="panel corner-brackets p-6">
                <div className="eyebrow mb-4">At a glance</div>
                <div className="grid grid-cols-2 gap-5">
                  {platform.keyStats.map((s) => (
                    <div key={s.label}>
                      <div className="font-mono text-[9px] text-slate-500 tracking-widest uppercase mb-1">{s.label}</div>
                      <div className="font-mono text-xl text-slate-100 tabular-nums">{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="relative py-16 md:py-24 px-6 md:px-10 border-t border-slate-800/50">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-4 flex items-start gap-4">
              <span className="section-index mt-1">/ 01</span>
              <div>
                <div className="eyebrow mb-2">Modules</div>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-50">
                  Six moving parts.
                </h2>
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                Each module is optional and independently deployable. Contract for the pieces you need, add the rest on a later wave.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platform.modules.map((m, i) => {
              const Icon = ICONS[m.icon] || CheckCircle2;
              return (
                <div key={m.name} className="panel hover-lift p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-md bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
                      <Icon size={18} strokeWidth={1.5} className="text-cyan-300" />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest text-slate-500">{`M${String(i + 1).padStart(2, "0")}`}</span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-50 tracking-tight leading-snug mb-2">{m.name}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{m.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features + Integrations + Compliance */}
      <section className="relative py-16 md:py-24 px-6 md:px-10 border-t border-slate-800/50">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8">
          {/* Features */}
          <div className="md:col-span-6 panel p-7">
            <div className="flex items-center gap-4 mb-5">
              <span className="section-index">/ 02</span>
              <div className="eyebrow">Features</div>
            </div>
            <h3 className="text-xl font-semibold text-slate-50 tracking-tight mb-6">Under the hood.</h3>
            <ul className="space-y-3">
              {platform.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                  <CheckCircle2 size={14} strokeWidth={1.6} className="text-emerald-400 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Integrations */}
          <div className="md:col-span-6 flex flex-col gap-6">
            <div className="panel p-7">
              <div className="flex items-center gap-4 mb-5">
                <span className="section-index">/ 03</span>
                <div className="eyebrow">Integrations</div>
              </div>
              <h3 className="text-xl font-semibold text-slate-50 tracking-tight mb-6">Where it plugs in.</h3>
              <div className="flex flex-wrap gap-2">
                {platform.integrations.map((it) => (
                  <span key={it} className="font-mono text-[11px] tracking-tight text-slate-200 border border-slate-800 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors px-2.5 py-1.5">
                    {it}
                  </span>
                ))}
              </div>
            </div>

            <div className="panel p-7">
              <div className="flex items-center gap-4 mb-5">
                <span className="section-index">/ 04</span>
                <div className="eyebrow">Compliance envelope</div>
              </div>
              <h3 className="text-xl font-semibold text-slate-50 tracking-tight mb-6">Frameworks satisfied.</h3>
              <div className="flex flex-wrap gap-2">
                {platform.compliance.map((c) => (
                  <span key={c} className="font-mono text-[10px] tracking-widest uppercase text-emerald-300 border border-emerald-400/25 bg-emerald-400/5 px-2.5 py-1.5">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="relative py-16 md:py-24 px-6 md:px-10 border-t border-slate-800/50">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-10 items-end">
            <div className="md:col-span-4 flex items-start gap-4">
              <span className="section-index mt-1">/ 05</span>
              <div>
                <div className="eyebrow mb-2">Use Cases</div>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-50">
                  Where it belongs.
                </h2>
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                We only pitch this platform where it has a defensible fit. If your program is not in the list below, we will tell you before you cut a PO.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {platform.useCases.map((u, i) => (
              <div key={u} className="panel hover-lift p-6">
                <div className="font-mono text-[10px] tracking-widest text-slate-500 mb-3">{`0${i + 1}`}</div>
                <div className="text-base text-slate-100 font-medium tracking-tight leading-snug">{u}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Model */}
      <section className="relative py-16 md:py-24 px-6 md:px-10 border-t border-slate-800/50">
        <div className="max-w-[1400px] mx-auto">
          <div className="panel corner-brackets p-8 md:p-10 grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-4">
              <div className="eyebrow mb-3">Engagement model</div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-50 leading-snug">
                How we deliver.
              </h3>
            </div>
            <div className="md:col-span-8 space-y-4 text-slate-300 text-sm md:text-base leading-relaxed">
              <p>{platform.engagement}</p>
              <div className="pt-4 flex flex-col sm:flex-row gap-3">
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-cyan-400 text-slate-950 text-[12px] font-mono uppercase tracking-[0.2em] hover:bg-white transition-colors"
                >
                  <Calendar size={14} /> Book a scoping call
                </a>
                <a
                  href="/#leadership"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-slate-700 text-slate-200 text-[12px] font-mono uppercase tracking-[0.2em] hover:border-cyan-400/60 hover:text-cyan-200 transition-colors"
                >
                  Meet the team <ChevronRight size={12} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prev / Next Nav */}
      <section className="relative py-14 px-6 md:px-10 border-t border-slate-800/50">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-4">
          <Link
            to={`/platforms/${prev}`}
            className="panel hover-lift p-6 group flex items-center gap-4"
          >
            <ArrowLeft size={18} className="text-cyan-300 shrink-0" />
            <div>
              <div className="eyebrow-muted mb-1">Previous platform</div>
              <div className="text-base text-slate-100 font-medium tracking-tight group-hover:text-cyan-200 transition-colors">
                {PLATFORM_DETAILS[prev].name}
              </div>
            </div>
          </Link>
          <Link
            to={`/platforms/${next}`}
            className="panel hover-lift p-6 group flex items-center gap-4 md:justify-end md:text-right"
          >
            <div className="order-1 md:order-none md:flex-1">
              <div className="eyebrow-muted mb-1">Next platform</div>
              <div className="text-base text-slate-100 font-medium tracking-tight group-hover:text-cyan-200 transition-colors">
                {PLATFORM_DETAILS[next].name}
              </div>
            </div>
            <ChevronRight size={18} className="text-cyan-300 shrink-0" />
          </Link>
        </div>
      </section>

      <DetailFooter platform={platform} />
    </div>
  );
}
